import { StreamWithChatRate } from '../types';
import { getHeatRatio, isHeatReady } from './heat';

export type SwitchMode = 'highest' | 'threshold' | 'random';

export interface SwitchSettings {
  switchMode: SwitchMode;
  /** Threshold mode: 10s rate must exceed the 5m average by this factor */
  threshold: number;
  languageFilter: string;
  minChatRate: number;
  gameBlacklist: string[];
  streamBlacklist: string[];
  favoriteStream: string;
}

export interface PickOptions {
  /** Random source for random mode (tests inject a fixed one) */
  random?: () => number;
  /** Every stream we know about — where the favourite is looked up */
  everything?: StreamWithChatRate[];
}

/** The auto-switch pool after the user's filters: language, floor rate, blacklists */
export const filterSwitchPool = (
  pool: StreamWithChatRate[],
  s: SwitchSettings,
): StreamWithChatRate[] => {
  let out = pool;
  if (s.languageFilter !== 'all') {
    out = out.filter(x => x.language === s.languageFilter);
  }
  if (s.minChatRate > 0) {
    out = out.filter(x => x.chatRate >= s.minChatRate);
  }
  if (s.gameBlacklist.length > 0) {
    const games = new Set(s.gameBlacklist);
    out = out.filter(x => !games.has(x.game_name));
  }
  if (s.streamBlacklist.length > 0) {
    const logins = new Set(s.streamBlacklist.map(l => l.toLowerCase()));
    out = out.filter(x => !logins.has(x.user_login.toLowerCase()));
  }
  return out;
};

const overThreshold = (x: StreamWithChatRate, threshold: number): boolean => {
  if (!isHeatReady(x)) return false;
  const rate5m = x.chatRate5m || 0;
  if (rate5m <= 0) return false;
  return (x.chatRate10s || 0) > rate5m * threshold;
};

/**
 * Where the player should go next, or null to stay put. Highest takes the
 * loudest chat, Random draws one, Threshold takes the hottest channel over
 * the line (only channels whose 5-minute baseline is ready count). When
 * nothing qualifies the favourite stream, if live anywhere, is the fallback.
 */
export const pickSwitchTarget = (
  pool: StreamWithChatRate[],
  s: SwitchSettings,
  opts: PickOptions = {},
): StreamWithChatRate | null => {
  let target: StreamWithChatRate | null = null;

  if (pool.length > 0) {
    if (s.switchMode === 'highest') {
      target = pool.reduce((best, x) => (x.chatRate > best.chatRate ? x : best), pool[0]);
    } else if (s.switchMode === 'random') {
      const random = opts.random ?? Math.random;
      target = pool[Math.floor(random() * pool.length)] ?? null;
    } else {
      const over = pool.filter(x => overThreshold(x, s.threshold));
      if (over.length > 0) {
        target = over.reduce((best, x) => (getHeatRatio(x) > getHeatRatio(best) ? x : best), over[0]);
      }
    }
  }

  if (!target && s.favoriteStream) {
    const fav = s.favoriteStream.toLowerCase();
    target = (opts.everything ?? pool).find(x => x.user_login.toLowerCase() === fav) ?? null;
  }

  return target;
};
